import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { format, subDays, parseISO } from 'date-fns';
import { Activity, Monitor, Globe, Clock, MapPin } from 'lucide-react';
import UserBehaviorInsights from './analytics/UserBehaviorInsights';
import GeoEngagement from './analytics/GeoEngagement';

const COLORS = ['hsl(240,95%,34%)', 'hsl(38,80%,55%)', 'hsl(218,11%,41%)', 'hsl(0,84%,60%)', 'hsl(160,60%,45%)', 'hsl(280,60%,50%)', 'hsl(200,70%,50%)', 'hsl(330,60%,50%)'];

const DEV_PATTERNS = ['lovable', '256712db', 'lovableproject'];

function isDevTraffic(row: any): boolean {
  const ref = (row.referrer || '').toLowerCase();
  const ua = (row.user_agent || '').toLowerCase();
  return DEV_PATTERNS.some(p => ref.includes(p) || ua.includes(p));
}

const Analytics = () => {
  const [days, setDays] = useState('30');
  const [showAllTraffic, setShowAllTraffic] = useState(false);

  const { data: rawAnalytics = [] } = useQuery({
    queryKey: ['site_analytics', days],
    queryFn: async () => {
      const since = subDays(new Date(), parseInt(days)).toISOString();
      const { data } = await supabase
        .from('site_analytics')
        .select('*')
        .gte('created_at', since)
        .order('created_at', { ascending: true });
      return data || [];
    },
  });

  const analytics = useMemo(() => {
    if (showAllTraffic) return rawAnalytics;
    return rawAnalytics.filter(r => !isDevTraffic(r));
  }, [rawAnalytics, showAllTraffic]);

  const { data: sectionViews = [] } = useQuery({
    queryKey: ['section_views', days],
    queryFn: async () => {
      const since = subDays(new Date(), parseInt(days)).toISOString();
      const { data } = await supabase
        .from('section_views')
        .select('*')
        .gte('created_at', since);
      return (data as any[]) || [];
    },
  });

  const dailyVisits = useMemo(() => {
    const map: Record<string, number> = {};
    analytics.forEach(r => {
      const day = format(parseISO(r.created_at), 'MMM dd');
      map[day] = (map[day] || 0) + 1;
    });
    return Object.entries(map).map(([date, visits]) => ({ date, visits }));
  }, [analytics]);

  const topPages = useMemo(() => {
    const map: Record<string, number> = {};
    analytics.forEach(r => { map[r.page_path] = (map[r.page_path] || 0) + 1; });
    return Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 10).map(([page, visits]) => ({ page, visits }));
  }, [analytics]);

  const browserDist = useMemo(() => {
    const map: Record<string, number> = {};
    analytics.forEach(r => { const b = r.browser || 'Unknown'; map[b] = (map[b] || 0) + 1; });
    return Object.entries(map).map(([name, value]) => ({ name, value }));
  }, [analytics]);

  const deviceDist = useMemo(() => {
    const map: Record<string, number> = {};
    analytics.forEach(r => { const d = r.device || 'Unknown'; map[d] = (map[d] || 0) + 1; });
    return Object.entries(map).map(([name, value]) => ({ name, value }));
  }, [analytics]);

  const topReferrers = useMemo(() => {
    const map: Record<string, number> = {};
    analytics.forEach(r => { if (r.referrer) { map[r.referrer] = (map[r.referrer] || 0) + 1; } });
    return Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 10);
  }, [analytics]);

  const countryDist = useMemo(() => {
    const map: Record<string, number> = {};
    analytics.forEach(r => { if (r.country) { map[r.country] = (map[r.country] || 0) + 1; } });
    return Object.entries(map).sort((a, b) => b[1] - a[1]).map(([name, value]) => ({ name, value }));
  }, [analytics]);

  const cityDist = useMemo(() => {
    const map: Record<string, number> = {};
    analytics.forEach(r => {
      const city = (r as any).city;
      if (city) { map[city] = (map[city] || 0) + 1; }
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]).map(([name, value]) => ({ name, value }));
  }, [analytics]);

  const avgDuration = useMemo(() => {
    const durations = analytics.map(r => (r as any).visit_duration_seconds).filter((d: any) => d && d > 0);
    if (durations.length === 0) return 0;
    return Math.round(durations.reduce((a: number, b: number) => a + b, 0) / durations.length);
  }, [analytics]);

  const formatDuration = (secs: number) => {
    if (secs < 60) return `${secs}s`;
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}m ${s}s`;
  };

  const devCount = rawAnalytics.length - analytics.length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold font-fraunces" style={{ color: 'var(--blue)' }}>Analytics</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {analytics.length} production visits in the last {days} days
            {!showAllTraffic && devCount > 0 && (
              <span className="ml-1">({devCount} dev visits hidden)</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Switch id="show-all" checked={showAllTraffic} onCheckedChange={setShowAllTraffic} />
            <Label htmlFor="show-all" className="text-xs text-muted-foreground cursor-pointer">Show dev traffic</Label>
          </div>
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="14">Last 14 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { label: 'Total Visits', value: analytics.length, icon: Activity },
          { label: 'Unique Sessions', value: new Set(analytics.map(a => a.session_id)).size, icon: Globe },
          { label: 'Avg. Duration', value: formatDuration(avgDuration), icon: Clock },
          { label: 'Top Browser', value: browserDist.sort((a,b) => b.value - a.value)[0]?.name || '-', icon: Monitor },
          { label: 'Top Country', value: countryDist[0]?.name || '-', icon: MapPin },
        ].map(({ label, value, icon: Icon }) => (
          <Card key={label} className="border-border">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg" style={{ background: 'var(--blue-light)' }}><Icon className="w-4 h-4" style={{ color: 'var(--blue)' }} /></div>
                <div>
                  <p className="text-xs text-muted-foreground">{label}</p>
                  <p className="text-lg font-bold" style={{ color: 'var(--ink)' }}>{value}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Daily Visits */}
      <Card className="border-border">
        <CardHeader><CardTitle className="text-sm" style={{ color: 'var(--ink)' }}>Daily Visits</CardTitle></CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={dailyVisits}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--line-slate)" />
              <XAxis dataKey="date" stroke="var(--slate)" fontSize={11} />
              <YAxis stroke="var(--slate)" fontSize={11} />
              <Tooltip contentStyle={{ background: 'var(--white)', border: '1px solid var(--line-slate)', borderRadius: 8, color: 'var(--ink)' }} />
              <Line type="monotone" dataKey="visits" stroke="hsl(38,80%,55%)" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Top Pages + Browser/Device */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="border-border">
          <CardHeader><CardTitle className="text-sm" style={{ color: 'var(--ink)' }}>Top Pages</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={topPages} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="var(--line-slate)" />
                <XAxis type="number" stroke="var(--slate)" fontSize={11} />
                <YAxis dataKey="page" type="category" stroke="var(--slate)" fontSize={11} width={120} />
                <Tooltip contentStyle={{ background: 'var(--white)', border: '1px solid var(--line-slate)', borderRadius: 8, color: 'var(--ink)' }} />
                <Bar dataKey="visits" fill="hsl(240,95%,34%)" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <div className="space-y-4">
          {[{ title: 'Browsers', data: browserDist }, { title: 'Devices', data: deviceDist }].map(({ title, data }) => (
            <Card key={title} className="border-border">
              <CardHeader className="pb-2"><CardTitle className="text-sm" style={{ color: 'var(--ink)' }}>{title}</CardTitle></CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  <ResponsiveContainer width={120} height={120}>
                    <PieChart>
                      <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={30} outerRadius={50}>
                        {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="space-y-1">
                    {data.map((d, i) => (
                      <div key={d.name} className="flex items-center gap-2 text-xs">
                        <div className="w-2 h-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                        <span className="text-muted-foreground">{d.name}: {d.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* User Behavior Insights */}
      <UserBehaviorInsights analytics={analytics} sectionViews={sectionViews} />

      {/* Geographic Engagement — country→city drill-down */}
      <GeoEngagement analytics={analytics} />

      {/* Referrers */}
      <Card className="border-border">
        <CardHeader><CardTitle className="text-sm" style={{ color: 'var(--ink)' }}>Top Referrers</CardTitle></CardHeader>
        <CardContent>
          {topReferrers.length === 0 ? (
            <p className="text-muted-foreground text-sm">No referrer data yet</p>
          ) : (
            <div className="space-y-2">
              {topReferrers.map(([ref, count]) => (
                <div key={ref} className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground truncate max-w-md">{ref}</span>
                  <span className="font-medium" style={{ color: 'var(--ink)' }}>{count}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Analytics;
